import {Audio} from '@remotion/media';
import React from 'react';
import {
	AbsoluteFill,
	Easing,
	Img,
	Sequence,
	interpolate,
	staticFile,
	useCurrentFrame,
} from 'remotion';

const SANS = '-apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", Arial, sans-serif';
const SERIF = '"Songti SC", "STSong", "Noto Serif SC", "SimSun", serif';
const MONO = '"SF Mono", "Menlo", "Courier New", monospace';
const RED = '#b3261e';
const GREEN = '#9ef0c0';
const CLAMP = {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'} as const;

const Grain: React.FC<{opacity?: number}> = ({opacity = 0.11}) => {
	const frame = useCurrentFrame();
	return (
		<div
			style={{
				position: 'absolute',
				inset: -60,
				backgroundImage: `url(${staticFile('s3/grain.png')})`,
				backgroundRepeat: 'repeat',
				backgroundPosition: `${(frame * 41) % 320}px ${(frame * 67) % 320}px`,
				opacity,
				mixBlendMode: 'overlay',
				pointerEvents: 'none',
			}}
		/>
	);
};

const Scanlines: React.FC<{opacity?: number}> = ({opacity = 0.5}) => (
	<div
		style={{
			position: 'absolute',
			inset: 0,
			backgroundImage:
				'repeating-linear-gradient(to bottom, rgba(0,0,0,0.32) 0px, rgba(0,0,0,0.32) 1px, transparent 1px, transparent 5px)',
			opacity,
			pointerEvents: 'none',
		}}
	/>
);

const Vignette: React.FC = () => (
	<div
		style={{
			position: 'absolute',
			inset: 0,
			background: 'radial-gradient(ellipse at center, transparent 38%, rgba(0,0,0,0.62) 100%)',
			pointerEvents: 'none',
		}}
	/>
);

const ScanBand: React.FC = () => {
	const frame = useCurrentFrame();
	return (
		<div
			style={{
				position: 'absolute',
				left: 0,
				right: 0,
				top: ((frame * 9) % 1300) - 110,
				height: 110,
				background: 'linear-gradient(to bottom, transparent, rgba(190,255,220,0.07), transparent)',
				pointerEvents: 'none',
			}}
		/>
	);
};

const Analog: React.FC<{children: React.ReactNode; grain?: number}> = ({children, grain = 0.12}) => (
	<AbsoluteFill>
		{children}
		<ScanBand />
		<Grain opacity={grain} />
		<Scanlines />
		<Vignette />
	</AbsoluteFill>
);

const fadeScene = (frame: number, outAt: number, duration: number) =>
	interpolate(frame, [0, 6, outAt, duration], [0, 1, 1, 0], CLAMP);

const typeText = (text: string, frame: number, start: number, framesPerChar = 2) =>
	text.slice(0, Math.max(0, Math.min(text.length, Math.floor((frame - start) / framesPerChar))));

const Subtitle: React.FC<{text: string; start: number; end: number; left?: number; bottom?: number}> = ({
	text,
	start,
	end,
	left = 120,
	bottom = 96,
}) => {
	const frame = useCurrentFrame();
	const opacity = interpolate(frame, [start, start + 10, end - 10, end], [0, 1, 1, 0], CLAMP);
	return (
		<div
			style={{
				position: 'absolute',
				left,
				bottom,
				display: 'flex',
				alignItems: 'stretch',
				gap: 22,
				opacity,
			}}
		>
			<div style={{width: 6, backgroundColor: RED, boxShadow: `0 0 18px ${RED}`}} />
			<div
				style={{
					fontFamily: SANS,
					fontSize: 44,
					fontWeight: 500,
					letterSpacing: 2,
					color: '#f3eee4',
					textShadow: '0 2px 20px rgba(0,0,0,0.95)',
					paddingTop: 2,
				}}
			>
				{text}
			</div>
		</div>
	);
};

const BootScreen: React.FC = () => {
	const frame = useCurrentFrame();
	const duration = 75;
	const opacity = fadeScene(frame, duration - 8, duration);
	const cursor = Math.floor(frame / 8) % 2 === 0;

	return (
		<AbsoluteFill style={{backgroundColor: '#020504', opacity}}>
			<Analog grain={0.07}>
				<div
					style={{
						padding: '142px 170px',
						fontFamily: MONO,
						fontSize: 38,
						lineHeight: '78px',
						letterSpacing: 2,
						color: GREEN,
						textShadow: '0 0 14px rgba(158,240,192,0.55)',
					}}
				>
					<div style={{opacity: 0.45, fontSize: 20, letterSpacing: 4}}>
						HA-AN PUBLIC SECURITY · ARCHIVE TERMINAL
					</div>
					<div style={{height: 46}} />
					<div>&gt; {typeText('影像调取终端 v3.2', frame, 10)}</div>
					<div>&gt; {typeText('案卷编号  HA-2008-0128', frame, 24)}</div>
					<div>&gt; {typeText('目标地点  槐安市第三殡仪馆 · 三号机', frame, 38, 1.6)}</div>
					<div>
						&gt; {typeText('正在读取影像…', frame, 54)}
						{cursor ? '█' : ''}
					</div>
				</div>
				<div
					style={{
						position: 'absolute',
						top: 112,
						right: 160,
						fontFamily: MONO,
						fontSize: 24,
						letterSpacing: 2,
						color: 'rgba(158,240,192,0.5)',
					}}
				>
					2008-01-28 05:12:33
				</div>
				<div
					style={{
						position: 'absolute',
						bottom: 96,
						left: 170,
						fontFamily: MONO,
						fontSize: 16,
						letterSpacing: 4,
						color: 'rgba(158,240,192,0.3)',
					}}
				>
					SOURCE: VHS-C / CAM 03 / 4:3 / SIGNAL LOST
				</div>
			</Analog>
		</AbsoluteFill>
	);
};

const CctvScene: React.FC = () => {
	const frame = useCurrentFrame();
	const duration = 90;
	const glitch =
		frame >= 38 && frame < 40 ? 22 : frame >= 68 && frame < 70 ? -13 : frame >= 84 ? 8 : 0;
	const zoom = 1.04 + frame * 0.0007;
	const opacity = fadeScene(frame, duration - 8, duration);

	return (
		<AbsoluteFill style={{backgroundColor: '#000', opacity}}>
			<div
				style={{
					position: 'absolute',
					inset: 0,
					transform: `translateX(${glitch}px) scale(${zoom})`,
				}}
			>
				<Img
					src={staticFile('s3/cctv.jpg')}
					style={{
						width: '100%',
						height: '100%',
						objectFit: 'cover',
						filter: 'grayscale(0.42) contrast(1.18) brightness(0.78) saturate(0.7)',
					}}
				/>
			</div>
			<div
				style={{
					position: 'absolute',
					inset: 0,
					backgroundColor: 'rgba(70,160,120,0.12)',
					mixBlendMode: 'color',
					pointerEvents: 'none',
				}}
			/>
			<Analog grain={0.16}>
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						height: 230,
						background: 'linear-gradient(to bottom, rgba(0,0,0,0.55), transparent)',
					}}
				/>
				<div
					style={{
						position: 'absolute',
						bottom: 0,
						left: 0,
						right: 0,
						height: 260,
						background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)',
					}}
				/>
				{[
					{left: 92, top: 88, borderRight: 'none', borderBottom: 'none'},
					{right: 92, top: 88, borderLeft: 'none', borderBottom: 'none'},
					{left: 92, bottom: 88, borderRight: 'none', borderTop: 'none'},
					{right: 92, bottom: 88, borderLeft: 'none', borderTop: 'none'},
				].map((corner, index) => (
					<div
						key={index}
						style={{
							position: 'absolute',
							width: 46,
							height: 46,
							border: '2px solid rgba(220,255,235,0.55)',
							...corner,
						}}
					/>
				))}
				<div
					style={{
						position: 'absolute',
						top: 96,
						left: 120,
						fontFamily: MONO,
						fontSize: 21,
						letterSpacing: 2,
						color: 'rgba(225,255,238,0.85)',
						textShadow: '0 0 10px rgba(120,255,190,0.5)',
					}}
				>
					CAM 03 · 西侧通道
				</div>
				<div
					style={{
						position: 'absolute',
						top: 96,
						right: 120,
						fontFamily: MONO,
						fontSize: 21,
						letterSpacing: 1.5,
						color: 'rgba(225,255,238,0.85)',
					}}
				>
					2008-01-28 05:12:33
				</div>
				<div
					style={{
						position: 'absolute',
						bottom: 96,
						right: 120,
						fontFamily: MONO,
						fontSize: 21,
						letterSpacing: 2,
						color: Math.floor(frame / 15) % 2 === 0 ? '#ff4a3d' : 'rgba(255,74,61,0.25)',
					}}
				>
					● REC
				</div>
				<div
					style={{
						position: 'absolute',
						bottom: 96,
						left: 120,
						fontFamily: MONO,
						fontSize: 16,
						letterSpacing: 3,
						color: 'rgba(225,255,238,0.35)',
					}}
				>
					05:12:33 / 00:37:12
				</div>
				<Subtitle text="那天夜里，三号机的监控断了 37 分钟。" start={22} end={82} />
			</Analog>
		</AbsoluteFill>
	);
};

const LedgerScene: React.FC = () => {
	const frame = useCurrentFrame();
	const duration = 90;
	const opacity = fadeScene(frame, duration - 8, duration);
	const stampProgress = interpolate(frame, [34, 42], [0, 1], {
		...CLAMP,
		easing: Easing.out(Easing.cubic),
	});
	const jitter = frame >= 20 && frame < 55 ? Math.sin(frame * 7.3) * 1.6 : 0;
	const rows = [
		['01', '李国强', '61', '一号机', '王德发'],
		['02', '陈秀兰', '58', '二号机', '王德发'],
		['03', '周　野', '29', '三号机', '———'],
	];

	return (
		<AbsoluteFill style={{backgroundColor: '#0b0906', opacity}}>
			<div style={{position: 'absolute', inset: -80, transform: 'rotate(-1.3deg) scale(1.1)'}}>
				<Img
					src={staticFile('s3/paper.jpg')}
					style={{width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.9) contrast(1.02)'}}
				/>
			</div>
			<div style={{position: 'absolute', inset: 0, backgroundColor: 'rgba(232,222,200,0.22)'}} />
			<div
				style={{
					position: 'absolute',
					left: 430,
					right: 430,
					top: 120,
					color: '#2a2118',
					transform: `translateX(${jitter}px)`,
				}}
			>
				<div style={{display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between'}}>
					<div>
						<div style={{fontFamily: SERIF, fontSize: 60, fontWeight: 700, letterSpacing: 8}}>
							槐安市第三殡仪馆
						</div>
						<div style={{fontFamily: SANS, fontSize: 24, letterSpacing: 4, opacity: 0.66, marginTop: 10}}>
							遗体登记簿 · 2008 年 1 月 28 日
						</div>
					</div>
					<div style={{fontFamily: MONO, fontSize: 17, letterSpacing: 1.5, opacity: 0.55}}>
						NO. HA-2008-0128
					</div>
				</div>
				<div style={{height: 2, backgroundColor: 'rgba(42,33,24,0.55)', margin: '30px 0 8px'}} />
			</div>
			<div
				style={{
					position: 'absolute',
					left: 430,
					right: 430,
					top: 300,
					color: '#2a2118',
					transform: `translateX(${jitter}px)`,
					fontFamily: SERIF,
				}}
			>
				<div
					style={{
						display: 'flex',
						fontSize: 20,
						letterSpacing: 4,
						opacity: 0.55,
						padding: '0 14px 14px',
						fontFamily: SANS,
					}}
				>
					<div style={{width: 110}}>序号</div>
					<div style={{width: 280}}>姓名</div>
					<div style={{width: 140}}>年龄</div>
					<div style={{width: 320}}>存放位置</div>
					<div style={{flex: 1}}>登记人</div>
				</div>
				{rows.map((row, rowIndex) => (
					<div
						key={row[0]}
						style={{
							display: 'flex',
							alignItems: 'center',
							fontSize: 34,
							letterSpacing: 3,
							padding: '20px 14px',
							borderTop: '1px solid rgba(42,33,24,0.22)',
							backgroundColor:
								rowIndex === 2
									? `rgba(179,38,30,${0.1 + Math.abs(Math.sin(frame / 6)) * 0.06})`
									: 'transparent',
						}}
					>
						<div style={{width: 110}}>{row[0]}</div>
						<div style={{width: 280, fontWeight: rowIndex === 2 ? 700 : 400}}>{row[1]}</div>
						<div style={{width: 140}}>{row[2]}</div>
						<div style={{width: 320, color: rowIndex === 2 ? RED : '#2a2118'}}>{row[3]}</div>
						<div style={{flex: 1, opacity: rowIndex === 2 ? 0.35 : 0.9}}>{row[4]}</div>
					</div>
				))}
				<div style={{height: 1, backgroundColor: 'rgba(42,33,24,0.22)'}} />
				<div
					style={{
						marginTop: 56,
						fontFamily: SERIF,
						fontSize: 26,
						letterSpacing: 2,
						color: 'rgba(42,33,24,0.62)',
					}}
				>
					备注：三号机于 2008-01-28 凌晨停电 37 分钟，监控录像缺失。
				</div>
				<div
					style={{
						marginTop: 26,
						display: 'flex',
						justifyContent: 'space-between',
						fontFamily: MONO,
						fontSize: 15,
						letterSpacing: 2,
						color: 'rgba(42,33,24,0.42)',
					}}
				>
					<span>登记人签字：王德发</span>
					<span>第 3 页 / 共 3 页</span>
				</div>
			</div>
			<div
				style={{
					position: 'absolute',
					right: 330,
					top: 470,
					padding: '12px 22px',
					border: `7px solid ${RED}`,
					color: RED,
					fontFamily: SERIF,
					fontSize: 78,
					fontWeight: 700,
					letterSpacing: 16,
					opacity: 0.86 * stampProgress,
					transform: `rotate(-13deg) scale(${1 + (1 - stampProgress) * 0.9})`,
					borderRadius: 4,
					textShadow: '0 0 2px rgba(179,38,30,0.4)',
					whiteSpace: 'nowrap',
				}}
			>
				查无此人
			</div>
			<div
				style={{
					position: 'absolute',
					bottom: 0,
					left: 0,
					right: 0,
					height: 250,
					background: 'linear-gradient(to top, rgba(6,5,4,0.82), transparent)',
				}}
			/>
			<Subtitle text="账本上，多了一具没人认领的尸体。" start={18} end={84} />
			<Grain opacity={0.07} />
			<Vignette />
		</AbsoluteFill>
	);
};

const PosterScene: React.FC = () => {
	const frame = useCurrentFrame();
	const duration = 75;
	const opacity = fadeScene(frame, duration - 8, duration);
	const toMemorial = interpolate(frame, [38, 48], [0, 1], CLAMP);
	const push = interpolate(frame, [0, duration], [1, 1.035], CLAMP);
	const printRotate = -1.5 + frame * 0.008;

	return (
		<AbsoluteFill style={{backgroundColor: '#0a0908', opacity}}>
			<div
				style={{
					position: 'absolute',
					inset: 0,
					background: 'radial-gradient(60% 70% at 32% 40%, rgba(255,214,160,0.13), transparent 70%)',
				}}
			/>
			<div
				style={{
					position: 'absolute',
					left: 210,
					top: 105,
					width: 592,
					height: 872,
					backgroundColor: '#e9e2d2',
					padding: 16,
					transform: `rotate(${printRotate}deg) scale(${push})`,
					boxShadow: '0 40px 120px rgba(0,0,0,0.85)',
				}}
			>
				<div style={{position: 'relative', width: '100%', height: '100%', overflow: 'hidden', backgroundColor: '#111'}}>
					<Img
						src={staticFile('s3/zhouye.jpg')}
						style={{position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover'}}
					/>
					<Img
						src={staticFile('s3/memorial.jpg')}
						style={{
							position: 'absolute',
							inset: 0,
							width: '100%',
							height: '100%',
							objectFit: 'cover',
							opacity: toMemorial,
							filter: 'saturate(0.75) contrast(1.05) brightness(0.9)',
						}}
					/>
					<div
						style={{
							position: 'absolute',
							inset: 0,
							background: 'linear-gradient(to top, rgba(0,0,0,0.42), transparent 34%)',
						}}
					/>
					<div
						style={{
							position: 'absolute',
							left: 22,
							bottom: 18,
							fontFamily: MONO,
							fontSize: 16,
							letterSpacing: 2,
							color: 'rgba(255,255,255,0.72)',
						}}
					>
						{toMemorial > 0.5 ? 'MEMORIAL 0042 / 2008' : '2007.10.26 / LAST PHOTO'}
					</div>
				</div>
			</div>
			<div
				style={{
					position: 'absolute',
					left: 900,
					top: 250,
					width: 760,
					color: '#efe9dd',
				}}
			>
				<div style={{fontFamily: MONO, fontSize: 18, letterSpacing: 4, color: RED, opacity: 0.9}}>
					{toMemorial > 0.5 ? 'CODE 0042 / ARCHIVE' : 'MISSING / 010-2008'}
				</div>
				<div style={{fontFamily: SERIF, fontSize: 122, fontWeight: 700, letterSpacing: 18, marginTop: 26}}>
					周野
				</div>
				<div style={{height: 2, width: 520, backgroundColor: 'rgba(239,233,221,0.28)', margin: '34px 0'}} />
				<div style={{fontFamily: SANS, fontSize: 30, lineHeight: '52px', color: 'rgba(239,233,221,0.78)'}}>
					{toMemorial > 0.5 ? (
						<>
							1979 — 2008
							<br />
							档案状态：<span style={{color: RED}}>查无此人</span>
						</>
					) : (
						<>
							29 岁 · 槐安市人
							<br />
							2008 年 1 月 28 日，在第三殡仪馆失踪
						</>
					)}
				</div>
				<div
					style={{
						marginTop: 60,
						fontFamily: MONO,
						fontSize: 16,
						letterSpacing: 3,
						color: 'rgba(239,233,221,0.32)',
					}}
				>
					{toMemorial > 0.5 ? 'NAME FOUND IN THE LEDGER' : 'NO BODY · NO RECORD · NO NAME'}
				</div>
			</div>
			<Subtitle
				text={toMemorial > 0.5 ? '十四年后，他的纪念馆上线了。' : '他是最后一个走进三号机的人。'}
				start={16}
				end={70}
				left={900}
			/>
			<Grain opacity={0.12} />
			<Scanlines opacity={0.35} />
			<Vignette />
		</AbsoluteFill>
	);
};

const SHOTS = [
	{src: 's3/dvr-door.jpg', label: '两套钟'},
	{src: 's3/dvr-mop.jpg', label: '拖　把'},
	{src: 's3/bridge.jpg', label: '桥　下'},
	{src: 's3/dvr-night.jpg', label: '第 42 夜'},
	{src: 's3/cctv.jpg', label: '三号机'},
];

const MontageShot: React.FC<{src: string; label: string; index: number}> = ({src, label, index}) => {
	const frame = useCurrentFrame();
	const flash = interpolate(frame, [0, 2], [0.5, 0], CLAMP);
	const zoom = 1.1 + frame * 0.004;
	const shift = index % 2 === 0 ? frame * 2 : -frame * 2;

	return (
		<AbsoluteFill style={{backgroundColor: '#000'}}>
			<Img
				src={staticFile(src)}
				style={{
					width: '100%',
					height: '100%',
					objectFit: 'cover',
					transform: `scale(${zoom}) translateX(${shift}px)`,
					filter: 'contrast(1.2) brightness(0.72) saturate(0.6)',
				}}
			/>
			<div style={{position: 'absolute', inset: 0, backgroundColor: 'rgba(8,10,14,0.35)'}} />
			<div
				style={{
					position: 'absolute',
					inset: 0,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					fontFamily: SERIF,
					fontSize: 140,
					fontWeight: 700,
					letterSpacing: 14,
					color: '#f4eee2',
					textShadow: '0 8px 50px rgba(0,0,0,0.9)',
				}}
			>
				{label}
			</div>
			<div style={{position: 'absolute', inset: 0, backgroundColor: '#fff', opacity: flash}} />
			<Grain opacity={0.18} />
			<Scanlines opacity={0.6} />
			<Vignette />
		</AbsoluteFill>
	);
};

const Montage: React.FC = () => (
	<AbsoluteFill style={{backgroundColor: '#000'}}>
		{SHOTS.map((shot, index) => (
			<Sequence key={shot.label} from={index * 12} durationInFrames={12}>
				<MontageShot src={shot.src} label={shot.label} index={index} />
			</Sequence>
		))}
	</AbsoluteFill>
);

const TitleScene: React.FC = () => {
	const frame = useCurrentFrame();
	const entrance = interpolate(frame, [0, 12], [0, 1], {
		...CLAMP,
		easing: Easing.out(Easing.cubic),
	});
	const titleScale = interpolate(entrance, [0, 1], [1.16, 1]);
	const titleBlur = interpolate(entrance, [0, 1], [22, 0]);
	const shake = frame < 5 ? Math.sin(frame * 9) * 8 : 0;
	const qrIn = interpolate(frame, [14, 26], [0, 1], {...CLAMP, easing: Easing.out(Easing.quad)});
	const fadeOut = interpolate(frame, [54, 60], [1, 0], CLAMP);

	return (
		<AbsoluteFill style={{backgroundColor: '#050505', opacity: fadeOut}}>
			<div
				style={{
					position: 'absolute',
					inset: 0,
					background:
						'radial-gradient(55% 45% at 30% 100%, rgba(179,38,30,0.3), transparent 70%), radial-gradient(40% 40% at 92% 8%, rgba(87,230,211,0.07), transparent 70%)',
				}}
			/>
			<div style={{position: 'absolute', top: 148, left: 176, fontFamily: MONO, fontSize: 17, letterSpacing: 5, color: 'rgba(255,255,255,0.34)'}}>
				槐安 · 2008 · 九站伪互联网
			</div>
			<div
				style={{
					position: 'absolute',
					left: 170,
					top: 300,
					transform: `translateX(${shake}px) scale(${titleScale})`,
					transformOrigin: 'left center',
					opacity: entrance,
					filter: `blur(${titleBlur}px)`,
				}}
			>
				<div style={{display: 'flex', alignItems: 'center', gap: 44}}>
					<div
						style={{
							width: 94,
							height: 94,
							border: `4px solid ${RED}`,
							color: RED,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							fontFamily: SERIF,
							fontSize: 30,
							fontWeight: 700,
							letterSpacing: 6,
							transform: 'rotate(-6deg)',
							lineHeight: 1.06,
							textAlign: 'center',
						}}
					>
						本格
						<br />
						推理
					</div>
					<div style={{fontFamily: SERIF, fontSize: 224, fontWeight: 700, letterSpacing: 26, color: '#f6f1e7', lineHeight: 1}}>
						三号机
					</div>
				</div>
				<div style={{marginTop: 26, marginLeft: 142, fontFamily: SANS, fontSize: 42, letterSpacing: 14, color: 'rgba(246,241,231,0.72)'}}>
					一本可以查的悬疑小说
				</div>
			</div>
			<Grain opacity={0.05} />
			<Vignette />
			<div
				style={{
					position: 'absolute',
					right: 160,
					bottom: 140,
					display: 'flex',
					alignItems: 'flex-end',
					gap: 34,
					opacity: qrIn,
					transform: `translateY(${(1 - qrIn) * 26}px)`,
				}}
			>
				<div style={{textAlign: 'right', paddingBottom: 8}}>
					<div style={{fontFamily: SANS, fontSize: 38, fontWeight: 600, letterSpacing: 6, color: '#f6f1e7'}}>扫码进入</div>
					<div style={{marginTop: 14, fontFamily: MONO, fontSize: 24, letterSpacing: 1, color: 'rgba(246,241,231,0.62)'}}>
						sanhaoji.pages.dev
					</div>
					<div style={{marginTop: 10, fontFamily: SANS, fontSize: 18, letterSpacing: 4, color: 'rgba(246,241,231,0.32)'}}>
						九站 · 四幕 · 已完结
					</div>
				</div>
				<div
					style={{
						width: 254,
						height: 254,
						borderRadius: 16,
						backgroundColor: '#f5f3ec',
						padding: 20,
						boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
					}}
				>
					<Img src={staticFile('qr.png')} style={{width: '100%', height: '100%', objectFit: 'contain'}} />
				</div>
			</div>
		</AbsoluteFill>
	);
};

export const SanhaojiTrailer: React.FC = () => {
	return (
		<AbsoluteFill style={{backgroundColor: '#000'}}>
			<Audio src={staticFile('trailer.wav')} />
			<Sequence from={0} durationInFrames={75}>
				<BootScreen />
			</Sequence>
			<Sequence from={75} durationInFrames={90}>
				<CctvScene />
			</Sequence>
			<Sequence from={165} durationInFrames={90}>
				<LedgerScene />
			</Sequence>
			<Sequence from={255} durationInFrames={75}>
				<PosterScene />
			</Sequence>
			<Sequence from={330} durationInFrames={60}>
				<Montage />
			</Sequence>
			<Sequence from={390} durationInFrames={60}>
				<TitleScene />
			</Sequence>
		</AbsoluteFill>
	);
};
